import { Group, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { rawApi } from '../../api/client';
import { detailKey, listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { EmptyNote, RecordScreen } from '../../components/RecordScreen';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { useBaseCurrency } from '../../hooks/useSeedLocation';
import { enumLabel } from '../../lib/enums';
import { formatMoney, formatQty } from '../../lib/format';
import { CountAreaButton } from './CountAreaButton';
import { LogWasteButton } from './LogWasteButton';
import { MoveStockButton } from './MoveStockButton';
import {
  ITEMS_PATH,
  LEDGER_PATH,
  landedCostPath,
  useCurrentFxRateMicro,
  useReasonNames,
  useStockOnHand,
} from './stock';
import { type LandedOffer, WhoSellsIt } from './WhoSellsIt';

interface StockItemRow {
  id: string;
  nameEn: string;
  nameEs?: string | null;
  category: string;
  stockUnit: string;
  avgCostBaseMinor: number;
  parLevel?: number | null;
  reorderPoint?: number | null;
  active?: boolean;
}

/**
 * One thing the bar buys, and everything you would do to it.
 *
 * The stock screens could only ever express collections. A stock item's row offered Edit and
 * nothing else; the row saying how much of it was on a shelf offered nothing at all. So counting
 * the walk-in, logging a dropped bottle, recording a case carried out to the bar and finding out
 * who sells it were four separate journeys that each began by picking that same item out of a list
 * of everything the business buys — from a screen that was already showing it.
 *
 * Everything here acts on the item this page is about. The forms open knowing it, and where the
 * action is against one shelf they open knowing that too, because the row that opened them said so.
 */
export function StockItemDetailPage() {
  const { itemId = '' } = useParams();
  const currency = useBaseCurrency();
  const areas = useEntityIndex('storageLocation');
  const reasonName = useReasonNames();
  const fxRateMicro = useCurrentFxRateMicro();

  // The list endpoint already returns every field this page needs, and is usually warm from the
  // stock list the reader arrived from, so no fetch-by-id is added to the contract for it.
  const itemsQuery = useQuery({
    queryKey: listKey(ITEMS_PATH),
    queryFn: () => rawApi<StockItemRow[]>('GET', ITEMS_PATH),
  });
  const item = (itemsQuery.data ?? []).find((r) => r.id === itemId);

  const onHand = useStockOnHand(undefined, itemId || undefined);

  const offersQuery = useQuery({
    queryKey: detailKey('landed-cost', `${itemId}:${fxRateMicro ?? 0}`),
    queryFn: () => rawApi<{ offers: LandedOffer[] }>('GET', landedCostPath(itemId, fxRateMicro!)),
    enabled: Boolean(itemId) && Boolean(fxRateMicro),
    staleTime: 60_000,
  });

  const areaRows = [...onHand.rows].sort((a, b) =>
    areas.label(a.storageLocationId).localeCompare(areas.label(b.storageLocationId)),
  );
  const total = areaRows.reduce((sum, r) => sum + r.qtyOnHand, 0);
  const unit = item?.stockUnit ?? '';
  const reading = (qty: number) => formatQty(qty, unit);
  const worthOf = (qty: number) => formatMoney(Math.round(qty * (item?.avgCostBaseMinor ?? 0)), currency);

  const target = item?.parLevel ?? item?.reorderPoint ?? null;
  const short = typeof item?.reorderPoint === 'number' && total <= item.reorderPoint;

  return (
    <RecordScreen
      backTo="/inventory"
      backLabel="All stock items"
      isLoading={itemsQuery.isLoading}
      /*
       * This page finds its item by searching the list it fetched, so "the request failed" and
       * "it is not in the list" arrive as the same empty answer. Told apart, because announcing
       * that an item is off the stock list when a fetch merely timed out is a sentence somebody
       * would act on.
       */
      loadFailed={itemsQuery.isError && !itemsQuery.data}
      loadFailedMessage="This item could not be read just now. Nothing has changed. Refresh the page to try again."
      notFound={!itemsQuery.isLoading && !itemsQuery.isError && !item}
      notFoundMessage="This item is no longer on the stock list."
      title={item?.nameEn ?? ''}
      subtitle={item ? enumLabel('inventoryCategory', item.category) : undefined}
      /*
       * Being short is a fact about the item, so it belongs beside its name rather than in a
       * banner further down the page — by the time a warning is below "Who sells it", the reader
       * has already decided what they came here to do. An item you no longer stock cannot be
       * short of anything, so the two never compete for the badge.
       */
      status={
        item?.active === false
          ? { label: 'No longer stocked', color: 'gray' }
          : short
            ? { label: 'Running low', color: 'yellow' }
            : undefined
      }
      actions={
        item ? (
          <>
            <LogWasteButton inventoryItemId={item.id} />
            <MoveStockButton inventoryItemId={item.id} />
          </>
        ) : undefined
      }
      facts={
        item
          ? [
              { label: 'On hand', value: onHand.isLoading ? null : reading(total), empty: 'Counting…' },
              { label: 'Worth', value: onHand.isLoading ? null : worthOf(total), empty: 'Counting…' },
              {
                label: 'Cost per unit',
                value: item.avgCostBaseMinor
                  ? `${formatMoney(item.avgCostBaseMinor, currency)} per ${unit || 'unit'}`
                  : null,
                empty: 'Never bought',
              },
              {
                label: 'Reorder at',
                value: typeof item.reorderPoint === 'number' ? reading(item.reorderPoint) : null,
                empty: 'Not set',
              },
              {
                label: 'Aim to hold',
                value: typeof item.parLevel === 'number' ? reading(item.parLevel) : null,
                empty: 'Not set',
              },
              // Only while it is short, and only when there is a level to fill back up to. The
              // arithmetic between "on hand" and "aim to hold" is the one thing the reader would
              // otherwise do in their head before ringing a vendor.
              ...(short && typeof target === 'number' && target > total
                ? [{ label: 'Order at least', value: reading(target - total) }]
                : []),
            ]
          : []
      }
      sections={
        item
          ? [
              {
                title: 'Where it is',
                description:
                  'Stock is counted separately in each area. Put a figure right by counting that area again, by logging what was thrown away, or by recording a move that never got written down.',
                children: onHand.isLoading ? (
                  <EmptyNote>Reading the shelf figures…</EmptyNote>
                ) : areaRows.length === 0 ? (
                  <EmptyNote>
                    None of this on any shelf yet. A figure appears here once some is received or counted.
                  </EmptyNote>
                ) : (
                  <Table.ScrollContainer minWidth={560}>
                    <Table verticalSpacing="sm" highlightOnHover>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Area</Table.Th>
                          <Table.Th>On hand</Table.Th>
                          <Table.Th>Worth</Table.Th>
                          <Table.Th />
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {areaRows.map((row) => (
                          <Table.Tr key={row.storageLocationId}>
                            <Table.Td>{areas.label(row.storageLocationId)}</Table.Td>
                            <Table.Td>
                              {/* Less than nothing on a shelf is not a rounding quirk — it means
                                  stock left that was never recorded arriving. */}
                              <Text
                                size="sm"
                                c={row.qtyOnHand < 0 ? 'red.7' : undefined}
                                fw={row.qtyOnHand < 0 ? 600 : undefined}
                              >
                                {reading(row.qtyOnHand)}
                              </Text>
                            </Table.Td>
                            <Table.Td>{worthOf(row.qtyOnHand)}</Table.Td>
                            <Table.Td>
                              {/*
                                Three small actions rather than a menu, because these ARE the
                                remedies the description names and each one already knows both the
                                item and the shelf. Counting is of an AREA, not of an item, so it
                                opens that area's whole sheet — which is what somebody standing in
                                front of the shelf is about to do anyway.
                              */}
                              <Group gap={4} wrap="nowrap" justify="flex-end">
                                <LogWasteButton
                                  compact
                                  label="Log waste"
                                  inventoryItemId={item.id}
                                  storageLocationId={row.storageLocationId}
                                />
                                <MoveStockButton
                                  compact
                                  label="Move"
                                  inventoryItemId={item.id}
                                  storageLocationId={row.storageLocationId}
                                />
                                <CountAreaButton
                                  compact
                                  label="Count this area"
                                  currency={currency}
                                  storageLocationId={row.storageLocationId}
                                />
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                ),
              },
              {
                title: 'Who sells it',
                // Not "their delivery charge spread over a typical order": the mainland vendors do
                // not charge for delivery at all, and what decides this comparison is the bill the
                // ferry or the forwarder sent afterwards.
                description:
                  'What each vendor charges, and what it comes to on the shelf once the shipping bills for their own deliveries are counted.',
                children: !fxRateMicro ? (
                  <EmptyNote>
                    Comparing a dollar price against a lempira one needs an exchange rate. Record today's rate
                    on the Prices screen and the vendors will be ranked here.
                  </EmptyNote>
                ) : offersQuery.isLoading ? (
                  <EmptyNote>Working out what each vendor comes to…</EmptyNote>
                ) : (
                  <WhoSellsIt
                    offers={offersQuery.data?.offers ?? []}
                    currency={currency}
                    unit={unit}
                    emptyNote="No vendor has a price on file for this. Record one on the vendor's own page and they will be compared here."
                  />
                ),
              },
            ]
          : []
      }
    >
      {item && (
        <CrudSection
          title="Its movements"
          description="Every delivery, sale, spill, move and count correction for this item, newest first. Nothing here can be edited — a mistake is put right by counting the area again or logging waste."
          listPath={LEDGER_PATH}
          listQuery={{ inventoryItemId: itemId }}
          emptyMessage="This has never moved."
          columns={[
            { accessor: 'at', title: 'When', unit: 'datetime', noWrap: true },
            { accessor: 'type', title: 'What happened', enumGroup: 'stockMovementType' },
            { accessor: 'storageLocationId', title: 'Area', reference: 'storageLocation' },
            {
              accessor: 'qty',
              title: 'Amount',
              render: (r) => (typeof r.qty === 'number' ? reading(r.qty) : '—'),
            },
            {
              accessor: 'costBaseMinor',
              title: 'Value',
              render: (r) => formatMoney(r.costBaseMinor as number, currency),
            },
            {
              accessor: 'reasonCode',
              title: 'Why',
              render: (r) => reasonName(r.reasonCode) || String(r.note ?? '') || '—',
            },
          ]}
        />
      )}
    </RecordScreen>
  );
}
